/**
 * Hooks personnalisés pour D6BattleWidget
 */

import {useCallback} from 'react';
import {GameState} from '../../../models/GameState';
import {PlayerStep} from '../../../models/PlayerStep';
import GameStateHelper from '../../../helpers/GameStateHelper';
import {getValueDice} from '../util/DiceUtil';
import {parseValue, evaluateD6Expression} from './D6BattleWidget.utils';

/**
 * Calcule les valeurs d'attributs pour un type donné
 */
export const useComputeAttributeValues = (gameState: GameState | null) => {
  return useCallback((typeName: string): string | null => {
    if (!gameState) return null;
    const allAttributes = [
      ...(gameState.data.objects || []),
      ...(gameState.data.skills || []),
      ...(gameState.data.butterflyEffects || []),
    ];
    const values: string[] = [];
    allAttributes.forEach((attr) => {
      if (attr.dropped === 0 && attr.attributeId?.includes(typeName)) {
        values.push(attr.value?.trim() || '');
      }
    });
    return values.length > 0 ? values.join(' + ') : null;
  }, [gameState]);
};

/**
 * RÈGLE FONDAMENTALE GRAVÉE DANS LE MARBRE :
 * - initValue contient l'expression D6 (ex: "1", "1D+6", "2D")
 * - On évalue initValue SANS lancer de dés pour les nombres simples ("1" → 1, pas 1D lancé)
 * - On stocke la valeur calculée dans initValue (remplace l'expression par le nombre)
 * - currentValue est initialisé avec initValue si elle est vide/null/undefined
 * - Une fois initialisé, currentValue ne change QUE pendant le combat (dégâts, etc.)
 * 
 * Fonction pour interpréter une expression D6 SANS lancer de dés pour les nombres simples
 * "1" doit donner 1, pas 1D lancé qui pourrait donner 7
 */
const interpretD6Value = (expression: string | number | null | undefined): number => {
  return evaluateD6Expression(expression);
};

/**
 * Hook pour charger les valeurs de combat
 * 
 * RÈGLE FONDAMENTALE GRAVÉE DANS LE MARBRE :
 * 1. initValue contient l'expression D6 de l'aventure (ex: "1", "1D+6", "2D")
 * 2. On évalue initValue SANS lancer de dés pour les nombres simples ("1" → 1, pas 1D lancé)
 * 3. On stocke la valeur calculée dans initValue (remplace l'expression par le nombre)
 * 4. currentValue est initialisé avec initValue si elle est vide/null/undefined
 * 5. Une fois initialisé, currentValue ne change QUE pendant le combat (dégâts, etc.)
 * 
 * ⚠️ NE JAMAIS CHANGER CETTE RÈGLE ⚠️
 */
export const useLoadCombatValues = (
  gameState: GameState | null,
  step: PlayerStep,
  setters: {
    setPlayerForce: (v: number) => void;
    setPlayerDamage: (v: number) => void;
    setPlayerLife: (v: number | null) => void;
    setPlayerMaxLife: (v: number | null) => void;
    setPlayerAttackBonus: (v: number) => void;
    setPlayerDamageBonus: (v: number) => void;
    setEnemyForce: (v: number) => void;
    setEnemyDamage: (v: number) => void;
    setEnemyLife: (v: number | null) => void;
    setEnemyMaxLife: (v: number | null) => void;
    setEnemyIntention: (v: string) => void;
    setDefeatMessage: (v: string | null) => void;
  },
  computeAttributeValues: (typeName: string) => string | null
) => {
  return useCallback(() => {
    if (!gameState) return;

    // ========== JOUEUR ==========
    
    // D6_FORCE
    const playerForceField = GameStateHelper.getAdventureExtraField(gameState, 'D6_FORCE');
    if (playerForceField) {
      // ═══════════════════════════════════════════════════════════════════════════════════════
      // 🚨 CE HOOK NE FAIT QUE LIRE - LES EXTRAFIELDS SONT DÉJÀ INITIALISÉS AU DÉMARRAGE 🚨
      // ═══════════════════════════════════════════════════════════════════════════════════════
      const currentValue = parseValue(playerForceField.currentValue) ?? 0;
      setters.setPlayerForce(currentValue);
    } else {
      setters.setPlayerForce(0);
    }

    // D6_DAMAGE
    const playerDamageField = GameStateHelper.getAdventureExtraField(gameState, 'D6_DAMAGE');
    if (playerDamageField) {
      // ═══════════════════════════════════════════════════════════════════════════════════════
      // 🚨 CE HOOK NE FAIT QUE LIRE - LES EXTRAFIELDS SONT DÉJÀ INITIALISÉS AU DÉMARRAGE 🚨
      // ═══════════════════════════════════════════════════════════════════════════════════════
      const currentValue = parseValue(playerDamageField.currentValue) ?? 0;
      setters.setPlayerDamage(currentValue);
    } else {
      setters.setPlayerDamage(0);
    }

    // D6_PV
    const playerLifeField = GameStateHelper.getAdventureExtraField(gameState, 'D6_PV');
    if (playerLifeField) {
      // ═══════════════════════════════════════════════════════════════════════════════════════
      // 🚨🚨🚨 CE HOOK NE FAIT QUE LIRE - LES EXTRAFIELDS SONT DÉJÀ INITIALISÉS PAR LE MOTEUR 🚨🚨🚨
      // ═══════════════════════════════════════════════════════════════════════════════════════
      // initValue est DÉJÀ évalué par le moteur au démarrage, pas besoin de réévaluer !
      // - initValue = valeur initiale évaluée (déjà calculée par le moteur)
      // - currentValue = valeur actuelle (change pendant le combat)
      // ═══════════════════════════════════════════════════════════════════════════════════════
      
      const playerPv = parseValue(playerLifeField.currentValue) ?? 0;
      const playerMaxPv = parseValue(playerLifeField.initValue) ?? playerPv;
      
      setters.setPlayerLife(playerPv);
      setters.setPlayerMaxLife(playerMaxPv);
    } else {
      setters.setPlayerLife(null);
      setters.setPlayerMaxLife(null);
    }

    // ========== ENNEMI ==========
    // Les stepExtraField sont initialisés par le moteur au chargement de l'étape
    const enemyForceField = GameStateHelper.getStepExtraField(gameState, step.numero, 'D6_FORCE_MONSTER');
    const enemyDamageField = GameStateHelper.getStepExtraField(gameState, step.numero, 'D6_DAMAGE_MONSTER');
    const enemyLifeField = GameStateHelper.getStepExtraField(gameState, step.numero, 'D6_PV_MONSTER');
    const enemyIntentionField = GameStateHelper.getStepExtraField(gameState, step.numero, 'D6_INTENTION');

    setters.setEnemyForce(enemyForceField ? parseValue(enemyForceField.currentValue) ?? 0 : 0);
    setters.setEnemyDamage(enemyDamageField ? parseValue(enemyDamageField.currentValue) ?? 0 : 0);
    setters.setEnemyIntention(enemyIntentionField?.currentValue || 'Attaque');

    // D6_PV_MONSTER - initValue est DÉJÀ évalué par le moteur, pas besoin de réévaluer !
    if (enemyLifeField) {
      const pnjPv = parseValue(enemyLifeField.currentValue) ?? 0;
      const pnjMaxPv = parseValue(enemyLifeField.initValue) ?? pnjPv;
      
      setters.setEnemyLife(pnjPv);
      setters.setEnemyMaxLife(pnjMaxPv);
    } else {
      setters.setEnemyLife(null);
      setters.setEnemyMaxLife(null);
    }

    // ========== BONUS (attributs/objets) ==========
    
    const attackBonus = computeAttributeValues('D6_ATTRIBUTE_ATTACK');
    if (attackBonus) {
      setters.setPlayerAttackBonus(getValueDice(attackBonus));
    } else {
      setters.setPlayerAttackBonus(0);
    }

    const damageBonus = computeAttributeValues('D6_ATTRIBUTE_DAMAGE');
    if (damageBonus) {
      setters.setPlayerDamageBonus(getValueDice(damageBonus));
    } else {
      setters.setPlayerDamageBonus(0);
    }

    // Vérifier défaite
    // Note: playerLife sera vérifié ailleurs car il peut être null au moment de l'appel
  }, [gameState, step, setters, computeAttributeValues]);
};
